import Seo from "../components/common/Seo";
import { Reveal, RevealStagger, RevealItem } from "../components/common/Reveal";
import { Eyebrow, SectionHeading } from "../components/common/Type";
import Cta from "../components/common/Cta";
import AgentVideoHero from "../components/sections/AgentVideoHero";
import AgentHardware from "../components/sections/AgentHardware";
import MythorosPricing from "../components/sections/MythorosPricing";
import AgentFeature from "../components/sections/AgentFeature";
import FaqSection from "../components/sections/FaqSection";
import FinalCTA from "../components/sections/FinalCTA";
import { Check, X } from "lucide-react";
import { skillPacks, agentLayers, standardAnswers } from "../lib/content";

const isList = [
  "A dedicated AI business system for your operation",
  "Configured around your workflows, documents, and standards",
  "Capable of approved actions across your business",
  "Operated under human oversight and permissions",
];
const isNotList = [
  "Not an off-the-shelf chatbot",
  "Not an Apple product or Mac Mini",
  "Not a replacement for your team",
  "Not a single-cloud, one-size-fits-all tool",
];

const integrations = ["CRM systems", "Email & calendar", "Communication tools", "Document storage", "Scheduling", "Business databases", "Approved AI providers", "Internal workflows"];
const support = ["Monitoring & refinement", "New skill rollouts", "Integration updates", "Performance reviews", "Team enablement", "Long-term roadmap"];

export default function DesktopAgent() {
  return (
    <>
      <Seo
        title="Mythoros© — Kratos Venture Group"
        description="Mythoros©, the Kratos Venture Group Desktop AI Agent — a dedicated AI business system: a compact desktop appliance configured with your knowledge, tools, automations, and industry skills. Local + cloud deployment, human oversight, approved integrations."
        path="/mythoros"
      />

      {/* Video-led scroll hero */}
      <AgentVideoHero />

      {/* Hardware / product */}
      <AgentHardware />

      {/* What it is / is not */}
      <section className="relative py-24 md:py-32 bg-kv-bg2/40 border-y border-kv-steel/10" data-testid="agent-is-isnot">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-2 gap-px bg-kv-steel/10 border border-kv-steel/15 rounded-[14px] overflow-hidden">
          <div className="bg-kv-bg p-8 md:p-12">
            <div className="font-mono text-xs uppercase tracking-[0.2em] text-kv-electric mb-6">What it is</div>
            <ul className="space-y-4">
              {isList.map((t) => (
                <li key={t} className="flex items-start gap-3">
                  <Check className="h-5 w-5 text-kv-electric mt-0.5 shrink-0" />
                  <span className="font-body text-base text-kv-off">{t}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-kv-bg p-8 md:p-12">
            <div className="font-mono text-xs uppercase tracking-[0.2em] text-kv-steel mb-6">What it is not</div>
            <ul className="space-y-4">
              {isNotList.map((t) => (
                <li key={t} className="flex items-start gap-3">
                  <X className="h-5 w-5 text-kv-steel mt-0.5 shrink-0" />
                  <span className="font-body text-base text-kv-grey">{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <div id="capabilities" className="scroll-mt-24"><AgentFeature /></div>

      {/* Architecture overview */}
      <section className="relative py-24 md:py-32 bg-kv-bg2/40 border-y border-kv-steel/10" data-testid="agent-architecture">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-2xl mb-14">
            <Eyebrow className="mb-5">Architecture overview</Eyebrow>
            <SectionHeading>Layered by design, configured to your business.</SectionHeading>
          </div>
          <Reveal className="mb-12">
            <div className="border border-kv-steel/25 bg-[#06070A] p-4 md:p-8 flex items-center justify-center">
              <img src="/assets/device/device-exploded.jpg" alt="Mythoros© — exploded internal architecture" className="w-full max-w-2xl h-auto object-contain" />
            </div>
          </Reveal>
          <RevealStagger className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {agentLayers.map((l, i) => (
              <RevealItem key={l} className="kv-card p-6 md:p-7">
                <span className="font-mono text-xs text-kv-royal">L{i + 1}</span>
                <h3 className="mt-4 font-display text-lg tracking-tight text-kv-off">{l}</h3>
              </RevealItem>
            ))}
          </RevealStagger>
        </div>
      </section>

      {/* Skill packs */}
      <section className="relative py-24 md:py-32" data-testid="agent-skill-packs">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-2xl mb-14">
            <Eyebrow className="mb-5">Industry skill packs</Eyebrow>
            <SectionHeading>Sector-specific skills, added as you grow.</SectionHeading>
          </div>
          <RevealStagger className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {skillPacks.map((s) => (
              <RevealItem key={s.name} className="kv-card p-6 md:p-7 flex items-center justify-between gap-3">
                <h3 className="font-display text-base md:text-lg tracking-tight text-kv-off">{s.name}</h3>
                {s.status === "coming" ? (
                  <span className="font-mono text-[9px] uppercase tracking-wide text-kv-steel border border-kv-steel/30 px-2 py-1 whitespace-nowrap">Coming Soon</span>
                ) : (
                  <span className="h-1.5 w-1.5 rounded-full bg-kv-electric shrink-0" />
                )}
              </RevealItem>
            ))}
          </RevealStagger>
        </div>
      </section>

      {/* Deployment + Security */}
      <section className="relative py-24 md:py-32 bg-kv-bg2/40 border-y border-kv-steel/10" data-testid="agent-deployment-security">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-2 gap-12">
          <Reveal>
            <Eyebrow className="mb-5">Local + cloud deployment</Eyebrow>
            <SectionHeading className="!text-2xl md:!text-3xl">Deployment options that fit your requirements.</SectionHeading>
            <p className="mt-5 font-body text-base text-kv-grey leading-relaxed">{standardAnswers.local}</p>
          </Reveal>
          <Reveal delay={0.1}>
            <Eyebrow className="mb-5">Security & permissions</Eyebrow>
            <SectionHeading className="!text-2xl md:!text-3xl">Access designed around your organization.</SectionHeading>
            <p className="mt-5 font-body text-base text-kv-grey leading-relaxed">{standardAnswers.security}</p>
          </Reveal>
        </div>
      </section>

      {/* Integrations + Support */}
      <section className="relative py-24 md:py-32" data-testid="agent-integrations-support">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-2 gap-16">
          <div>
            <Eyebrow className="mb-6">Approved integrations</Eyebrow>
            <div className="flex flex-wrap gap-2">
              {integrations.map((i) => (
                <span key={i} className="font-body text-sm text-kv-grey border border-kv-steel/25 px-4 py-2 hover:border-kv-electric/40 transition-colors">{i}</span>
              ))}
            </div>
          </div>
          <div>
            <Eyebrow className="mb-6">Ongoing support</Eyebrow>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {support.map((s) => (
                <li key={s} className="flex items-center gap-2 font-body text-sm text-kv-off">
                  <span className="h-1 w-1 rounded-full bg-kv-electric" /> {s}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <MythorosPricing />

      <FaqSection heading="Mythoros© — questions." />
      <FinalCTA />
    </>
  );
}
